/**
 * Family-columns board (WSRADM-0032). Structure + allocations save together.
 * Product pane is a slot for WSRADM-0033. Cards are not draggable; copy-into only.
 */
import {
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type DragEvent,
} from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormHelperText,
  IconButton,
  Paper,
  Skeleton,
  Snackbar,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import {
  getDimensionBoard,
  getDimensions,
  putDimensionBoard,
  putDimensions,
} from '@/api/dimensions/dimensionsApi';
import { slugFromDisplayName } from '@/api/dimensions/slug';
import type {
  DimensionBoard as DimensionBoardDto,
  DimensionStructure,
  DimensionStructureSaved,
  StructureFamily,
} from '@/api/dimensions/types';
import { ErrorDetails } from '@/components/ErrorDetails';
import { FAMILY_PALETTE, familyColorOrNeutral } from '@/components/familyPalette';
import { EM_DASH, textOrDash } from '@/components/rosterFormat';
import { useCopy } from '@/hooks/useCopy';
import { useResource, type ResourceStatus } from '@/hooks/useResource';
import { Icon } from '@/theme';
import {
  SAVE_ERROR_FALLBACK,
  cloneDraft,
  countAllocChanges,
  familyAllocChanged,
  isCompleteEcho,
  isUncertainFailure,
  mapLoadError,
  mapSaveError as mapBoardSaveError,
  membershipsFromDraft,
  sameMemberships,
  seedDraft,
  type Draft,
  type SaveErrorKey,
} from './boardModel';
import { CatalogueStatusChip } from './CatalogueStatusChip';
import { effectiveStatus, isFaded, isHideableRetired } from './catalogueStatus';
import { UnsavedChangesDialog } from './dialogs';
import { ProductPane } from './ProductPane';
import {
  STRUCTURE_SAVE_ERROR_FALLBACK,
  familySlugsOf,
  mapSaveError as mapStructureSaveError,
  maxSortOrder,
  nextPaletteColor,
  sameStructureBody,
  toPutBody,
  type StructureSaveErrorKey,
} from './structureModel';

const NAV_WIDTH = 248;
const PICKER_MAX = 80;

export interface FamilyBoardProps {
  slug: string;
  structure: DimensionStructure | null;
  structureStatus: ResourceStatus;
  refetchStructure: () => void;
  onStructureSaved: (next: DimensionStructureSaved) => void;
  onBack: () => void;
  onDirtyChange: (dirty: boolean) => void;
}

function structuresEqualForDim(
  a: StructureFamily[],
  b: StructureFamily[],
): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    const x = a[i]!;
    const y = b[i]!;
    if (
      x.slug !== y.slug ||
      x.displayName !== y.displayName ||
      x.sortOrder !== y.sortOrder ||
      x.color !== y.color ||
      x.role !== y.role
    ) {
      return false;
    }
  }
  return true;
}

export function FamilyBoard({
  slug,
  structure,
  structureStatus,
  refetchStructure,
  onStructureSaved,
  onBack,
  onDirtyChange,
}: FamilyBoardProps) {
  const { t } = useCopy();
  const headingId = useId();
  const dimension = structure?.dimensions.find((d) => d.slug === slug) ?? null;
  const readOnly = dimension?.readOnly === true;
  const backIssued = useRef(false);

  useEffect(() => {
    if (structureStatus !== 'ready' || structure === null) return;
    if (dimension !== null) {
      backIssued.current = false;
      return;
    }
    if (!backIssued.current) {
      backIssued.current = true;
      onBack();
    }
  }, [structure, structureStatus, dimension, onBack]);

  const boardRes = useResource(
    () => getDimensionBoard(slug, { includeRetired: true }),
    [slug],
  );
  const [board, setBoard] = useState<DimensionBoardDto | null>(null);
  useEffect(() => {
    setBoard(boardRes.data);
  }, [boardRes.data]);

  const [draft, setDraft] = useState<Draft>(new Map());
  const [seed, setSeed] = useState<Draft>(new Map());
  const [familiesDraft, setFamiliesDraft] = useState<StructureFamily[]>([]);
  const [structureDirty, setStructureDirty] = useState(false);
  const [staleStructure, setStaleStructure] = useState(false);
  const [saving, setSaving] = useState(false);
  const [boardSaveError, setBoardSaveError] = useState<SaveErrorKey | null>(null);
  const [structureSaveError, setStructureSaveError] = useState<StructureSaveErrorKey | null>(
    null,
  );
  // The caught errors behind the banners above, for the copyable details.
  const [boardSaveFailure, setBoardSaveFailure] = useState<unknown>(null);
  const [structureSaveFailure, setStructureSaveFailure] = useState<unknown>(null);
  const [boardOnlyError, setBoardOnlyError] = useState(false);
  const [notice, setNotice] = useState<'saved' | 'savedQueued' | null>(null);
  const [backPrompt, setBackPrompt] = useState(false);
  const [dragOverSlug, setDragOverSlug] = useState<string | null>(null);
  const [menuSlug, setMenuSlug] = useState<string | null>(null);
  const [confirmDelFam, setConfirmDelFam] = useState<string | null>(null);
  const [pickerSlug, setPickerSlug] = useState<string | null>(null);
  const [pickerSearch, setPickerSearch] = useState('');
  const [pickerSelected, setPickerSelected] = useState<Set<number>>(new Set());
  const [newFamilyName, setNewFamilyName] = useState('');
  const [showRetired, setShowRetired] = useState(false);
  const [selection, setSelection] = useState<Set<number>>(() => new Set());
  const [dragIds, setDragIds] = useState<number[] | null>(null);
  const prevStructureRef = useRef<DimensionStructure | null>(null);
  // Bumped by every edit / discard / save so a slow background re-check never
  // overwrites a newer draft.
  const editEpochRef = useRef(0);

  // Allocation dirtiness is derived from the data (never a flag set from an event
  // handler), so every edit path — drag, menu, ×, picker, bulk — surfaces the bar and
  // an undone edit returns the board to clean.
  const allocChangeCount = useMemo(() => countAllocChanges(seed, draft), [seed, draft]);
  const allocDirty = allocChangeCount > 0;
  const dirty = allocDirty || structureDirty;

  useEffect(() => {
    if (board === null) return;
    const seeded = seedDraft(board);
    setDraft(seeded);
    setSeed(seeded);
    setBoardSaveError(null);
    setBoardOnlyError(false);
  }, [board]);

  useLayoutEffect(() => {
    if (dimension === null) return;
    if (structureDirty) return;
    setFamiliesDraft(dimension.families.map((f) => ({ ...f })));
  }, [dimension, structureDirty]);

  useEffect(() => {
    const prev = prevStructureRef.current;
    prevStructureRef.current = structure;
    if (prev === null || structure === null || prev === structure) return;
    if (structureDirty) {
      setStaleStructure(true);
      return;
    }
    const dim = structure.dimensions.find((d) => d.slug === slug);
    if (dim) {
      setFamiliesDraft(dim.families.map((f) => ({ ...f })));
      setStaleStructure(false);
    }
  }, [structure, slug, structureDirty]);

  useEffect(() => {
    onDirtyChange(dirty);
    return () => onDirtyChange(false);
  }, [dirty, onDirtyChange]);

  function onAllocEdit() {
    editEpochRef.current += 1;
    setBoardSaveError(null);
    setBoardOnlyError(false);
  }

  function markStructureDirty(next: StructureFamily[]) {
    editEpochRef.current += 1;
    setFamiliesDraft(next);
    const baseline = dimension?.families ?? [];
    setStructureDirty(!structuresEqualForDim(next, baseline));
    setStructureSaveError(null);
  }

  function addTo(productId: number, familySlug: string) {
    addToMany([productId], familySlug);
  }

  function removeFrom(productId: number, familySlug: string) {
    removeFromMany([productId], familySlug);
  }

  function addToMany(ids: number[], familySlug: string) {
    setDraft((current) => {
      const toAdd = ids.filter((id) => current.get(id)?.has(familySlug) === false);
      if (toAdd.length === 0) return current;
      const next = cloneDraft(current);
      for (const productId of toAdd) next.get(productId)?.add(familySlug);
      return next;
    });
    onAllocEdit();
  }

  function removeFromMany(ids: number[], familySlug: string) {
    setDraft((current) => {
      const toRemove = ids.filter((id) => current.get(id)?.has(familySlug) === true);
      if (toRemove.length === 0) return current;
      const next = cloneDraft(current);
      for (const productId of toRemove) next.get(productId)?.delete(familySlug);
      return next;
    });
    onAllocEdit();
  }

  function discard() {
    editEpochRef.current += 1;
    if (board !== null) {
      const seeded = seedDraft(board);
      setDraft(seeded);
      setSeed(seeded);
    }
    if (dimension !== null) {
      setFamiliesDraft(dimension.families.map((f) => ({ ...f })));
    }
    setStructureDirty(false);
    setStaleStructure(false);
    setBoardSaveError(null);
    setStructureSaveError(null);
    setBoardOnlyError(false);
    setMenuSlug(null);
    setConfirmDelFam(null);
    setPickerSlug(null);
    setSelection(new Set());
  }

  function structureWithFamilies(): DimensionStructure | null {
    if (structure === null || dimension === null) return null;
    return {
      dimensions: structure.dimensions.map((d) =>
        d.slug === slug ? { ...d, families: familiesDraft.map((f) => ({ ...f })) } : d,
      ),
    };
  }

  /**
   * After a save fails with something other than a 4xx (a 5xx, a network failure, a
   * timeout) the write may still have landed — wsr-api commits the memberships and
   * only then queues the rebuild. Re-read the structure and the board
   * (`includeRetired=true`, as always); when both equal what was sent, the save
   * landed: clear the banner and seed from the re-read. Otherwise leave the banner
   * and the draft untouched. Never re-sends the PUT.
   */
  async function recheckAfterFailure(sent: {
    board: DimensionBoardDto;
    draft: Draft;
    structure: DimensionStructure;
  }) {
    const epoch = editEpochRef.current;
    try {
      const [freshStructure, freshBoard] = await Promise.all([
        getDimensions(),
        getDimensionBoard(slug, { includeRetired: true }),
      ]);
      if (editEpochRef.current !== epoch) return;
      if (!sameStructureBody(freshStructure, sent.structure)) return;
      const fresh = { board: freshBoard, draft: seedDraft(freshBoard) };
      if (!sameMemberships({ board: sent.board, draft: sent.draft }, fresh)) return;

      setBoard(freshBoard);
      onStructureSaved({ dimensions: freshStructure.dimensions, build: null });
      const freshDim = freshStructure.dimensions.find((d) => d.slug === slug);
      if (freshDim) setFamiliesDraft(freshDim.families.map((f) => ({ ...f })));
      setStructureDirty(false);
      setStaleStructure(false);
      setBoardSaveError(null);
      setStructureSaveError(null);
      setBoardOnlyError(false);
      setSelection(new Set());
      setNotice('saved');
    } catch {
      // The re-check is best-effort: on any failure the banner and draft stay.
    }
  }

  async function onSave() {
    if (board === null || structure === null || dimension === null || readOnly) return;
    editEpochRef.current += 1;
    const sentBoard = board;
    const sentDraft = draft;
    const sentStructure = structureWithFamilies();
    setSaving(true);
    setBoardSaveError(null);
    setStructureSaveError(null);
    setBoardOnlyError(false);

    const doStructure = structureDirty;
    const doAlloc = allocDirty;
    let lastBuild: unknown = null;

    try {
      if (doStructure) {
        const bodyStruct = structureWithFamilies();
        if (bodyStruct === null) {
          setSaving(false);
          return;
        }
        const response = await putDimensions(toPutBody(bodyStruct), {
          build: doAlloc ? false : true,
        });
        lastBuild = response.build;
        onStructureSaved(response);
        setStructureDirty(false);
        setStaleStructure(false);
        const savedDim = response.dimensions.find((d) => d.slug === slug);
        if (savedDim) {
          setFamiliesDraft(savedDim.families.map((f) => ({ ...f })));
        }
      }

      if (doAlloc) {
        try {
          const response = await putDimensionBoard(
            slug,
            { memberships: membershipsFromDraft(board, draft) },
            { build: true, includeRetired: true },
          );
          lastBuild = response.build;
          if (isCompleteEcho(board, response)) {
            const { build: _build, ...saved } = response;
            setBoard(saved);
          } else {
            boardRes.refetch();
          }
          setSelection(new Set());
        } catch (err) {
          setSaving(false);
          setBoardSaveFailure(err);
          if (doStructure) {
            setBoardOnlyError(true);
            setBoardSaveError(mapBoardSaveError(err));
          } else {
            setBoardSaveError(mapBoardSaveError(err));
          }
          if (isUncertainFailure(err) && sentStructure !== null) {
            void recheckAfterFailure({
              board: sentBoard,
              draft: sentDraft,
              structure: sentStructure,
            });
          }
          return;
        }
      }

      setSaving(false);
      if (doStructure && !doAlloc) setSelection(new Set());
      setNotice(lastBuild !== null && lastBuild !== undefined ? 'savedQueued' : 'saved');
    } catch (err) {
      setSaving(false);
      setStructureSaveFailure(err);
      setStructureSaveError(mapStructureSaveError(err));
      if (isUncertainFailure(err) && sentStructure !== null) {
        void recheckAfterFailure({
          board: sentBoard,
          draft: sentDraft,
          structure: sentStructure,
        });
      }
    }
  }

  function onDropFamily(familySlug: string, event: DragEvent) {
    event.preventDefault();
    setDragOverSlug(null);
    if (readOnly || saving) return;
    const raw = event.dataTransfer.getData('text/plain');
    for (const part of raw.split(',')) {
      const id = Number(part.trim());
      if (!Number.isNaN(id)) addTo(id, familySlug);
    }
  }

  function createFamily() {
    const name = newFamilyName.trim();
    if (name === '' || structure === null) return;
    const taken = new Set([...familySlugsOf(structure), ...familiesDraft.map((f) => f.slug)]);
    const familySlug = slugFromDisplayName(name, taken);
    const sortOrder = maxSortOrder(familiesDraft) + 1;
    const family: StructureFamily = {
      slug: familySlug,
      displayName: name,
      sortOrder: sortOrder < 0 ? 0 : sortOrder,
      color: nextPaletteColor(familiesDraft),
      role: null,
      productCount: 0,
    };
    markStructureDirty([...familiesDraft, family]);
    setNewFamilyName('');
  }

  function deleteFamily(familySlug: string) {
    markStructureDirty(familiesDraft.filter((f) => f.slug !== familySlug));
    let hadMembership = false;
    for (const slugs of draft.values()) {
      if (slugs.has(familySlug)) {
        hadMembership = true;
        break;
      }
    }
    if (hadMembership) {
      setDraft((current) => {
        const next = cloneDraft(current);
        for (const slugs of next.values()) {
          slugs.delete(familySlug);
        }
        return next;
      });
      onAllocEdit();
    }
    setConfirmDelFam(null);
    setMenuSlug(null);
  }

  if (structure === null || dimension === null) {
    if (structureStatus === 'loading') {
      return <Skeleton variant="rounded" height={240} role="status" />;
    }
    return null;
  }

  if (boardRes.status === 'loading' && board === null) {
    return <Skeleton variant="rounded" height={240} role="status" />;
  }

  const loadError = boardRes.status === 'error' ? mapLoadError(boardRes.error) : null;
  if (boardRes.status === 'error') {
    const copy =
      loadError === 'dimensionGone'
        ? t('config.board.err.dimensionGone', SAVE_ERROR_FALLBACK.dimensionGone)
        : loadError === 'tooLarge'
          ? t('config.board.err.tooLarge', SAVE_ERROR_FALLBACK.tooLarge)
          : t('config.board.err.loadFailed', 'Could not load the board.');
    return (
      <Alert
        severity="error"
        action={
          loadError === 'dimensionGone' ? (
            <Button color="inherit" size="small" onClick={refetchStructure}>
              {t('config.board.reloadStructure', 'Refresh dimensions')}
            </Button>
          ) : (
            <Button color="inherit" size="small" onClick={boardRes.refetch}>
              {t('common.retry', 'Retry')}
            </Button>
          )
        }
      >
        {copy}
        <ErrorDetails error={boardRes.error} headline={copy} intent="read" />
      </Alert>
    );
  }

  if (board === null) return null;

  const busy = saving;
  // Curated headlines. Only the generic `saveFailed` keys say the draft is kept
  // (it always is); mapped codes keep their own copy and actions.
  const boardOnlyHeadline = t(
    'config.board.err.boardOnly',
    'Families were saved; product changes were not. Fix and save again.',
  );
  const structureHeadline =
    structureSaveError === null
      ? ''
      : structureSaveError === 'saveFailed'
        ? t(
            'config.classification.err.saveFailedKeep',
            'Could not save the classification structure. Your changes are still here — try again.',
          )
        : t(
            `config.classification.err.${structureSaveError}`,
            STRUCTURE_SAVE_ERROR_FALLBACK[structureSaveError],
          );
  const boardHeadline =
    boardSaveError === null
      ? ''
      : boardSaveError === 'saveFailed'
        ? t(
            'config.board.err.saveFailedKeep',
            'Could not save the board. Your changes are still here — try again.',
          )
        : t(`config.board.err.${boardSaveError}`, SAVE_ERROR_FALLBACK[boardSaveError]);
  const pendingFam = structureDirty
    ? Math.max(1, Math.abs(familiesDraft.length - (dimension.families.length)))
    : 0;
  const familyProductCount = new Map(board.families.map((f) => [f.slug, f.productCount]));
  // Unassigned honours Show retired (what you can pick); a family column never
  // does — it lists every member, current or not. `hiddenRetired` is a display
  // count of what the switch is hiding, not a server answer.
  const unassignedAll = board.products.filter(
    (p) => (draft.get(p.sourceProductId)?.size ?? 0) === 0,
  );
  const unassignedShown = unassignedAll.filter(
    (p) => showRetired || !isHideableRetired(effectiveStatus(p)),
  );
  const hiddenRetired = unassignedAll.length - unassignedShown.length;

  const pickerFamily = pickerSlug === null ? null : familiesDraft.find((f) => f.slug === pickerSlug);
  const pickerCandidates =
    pickerSlug === null
      ? []
      : board.products.filter((p) => draft.get(p.sourceProductId)?.has(pickerSlug) !== true);
  const pickerFiltered = pickerCandidates.filter((p) => {
    if (pickerSearch.trim() === '') return true;
    const name = (p.name ?? '').toLowerCase();
    return name.includes(pickerSearch.trim().toLowerCase());
  });
  const pickerShown = pickerFiltered.slice(0, PICKER_MAX);
  const pickerOverflow = pickerFiltered.length > PICKER_MAX;

  return (
    <Box component="section" aria-labelledby={headingId} sx={{ pb: dirty && !readOnly ? 10 : 0 }}>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 1, flexWrap: 'wrap' }}>
        <Button
          data-testid="board-back"
          onClick={() => {
            if (dirty) setBackPrompt(true);
            else onBack();
          }}
        >
          {t('common.back', 'Back')}
        </Button>
        <Typography variant="h1" component="h1" id={headingId} sx={{ flex: 1 }}>
          {dimension.displayName}
        </Typography>
        {readOnly ? (
          <Chip size="small" icon={<Icon name="lock" />} label={t('config.classification.locked', 'Locked')} />
        ) : null}
      </Stack>
      <Typography color="text.secondary" sx={{ mb: 2 }}>
        {readOnly
          ? t(
              'config.board.readOnlyLede',
              'WStudio categories — read-only. Open a revenue grouping to assign products.',
            )
          : t(
              'config.board.lede',
              'Assign products to families. A product can belong to more than one family.',
            )}
      </Typography>

      {staleStructure ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          {t(
            'config.board.staleStructure',
            'The grouping changed elsewhere. Your family edits are kept; save or discard to continue.',
          )}
        </Alert>
      ) : null}

      {boardOnlyError ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {boardOnlyHeadline}
          <ErrorDetails error={boardSaveFailure} headline={boardOnlyHeadline} />
        </Alert>
      ) : null}

      {structureSaveError !== null ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {structureHeadline}
          <ErrorDetails error={structureSaveFailure} headline={structureHeadline} />
        </Alert>
      ) : null}

      {boardSaveError !== null && !boardOnlyError ? (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            boardSaveError === 'staleBoard' ? (
              <Button color="inherit" size="small" onClick={boardRes.refetch} disabled={busy}>
                {t('config.board.reload', 'Reload')}
              </Button>
            ) : boardSaveError === 'dimensionGone' ? (
              <Button color="inherit" size="small" onClick={refetchStructure} disabled={busy}>
                {t('config.board.reloadStructure', 'Refresh dimensions')}
              </Button>
            ) : undefined
          }
        >
          {boardHeadline}
          <ErrorDetails error={boardSaveFailure} headline={boardHeadline} />
        </Alert>
      ) : null}

      <Box
        sx={{
          display: 'flex',
          gap: '16px',
          alignItems: 'flex-start',
          flexDirection: { xs: 'column', lg: 'row' },
          '@media (max-width: 1180px)': { flexDirection: 'column' },
        }}
      >
        {readOnly ? null : (
          <Box
            data-testid="product-pane-slot"
            sx={{
              width: 392,
              flex: 'none',
              '@media (max-width: 1180px)': { width: '100%' },
            }}
          >
            <ProductPane
              products={board.products}
              families={familiesDraft.map((f) => ({
                slug: f.slug,
                displayName: f.displayName,
                color: f.color,
                productCount: familyProductCount.get(f.slug) ?? f.productCount,
              }))}
              draft={draft}
              showRetired={showRetired}
              onShowRetiredChange={setShowRetired}
              addTo={addToMany}
              removeFrom={removeFromMany}
              onDragIdsChange={setDragIds}
              selection={selection}
              onSelectionChange={setSelection}
            />
          </Box>
        )}

        <Box
          sx={{
            display: 'flex',
            gap: 2,
            overflowX: 'auto',
            flex: 1,
            minWidth: 0,
            pb: 1,
            alignItems: 'flex-start',
          }}
        >
          {/* Unassigned */}
          <Paper
            variant="outlined"
            data-testid="col-unassigned"
            sx={{
              width: 246,
              flex: 'none',
              borderStyle: 'dashed',
              p: 1.5,
            }}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
            }}
          >
            <Typography variant="subtitle1">
              {t('config.board.unassignedCol', 'Unassigned')}
            </Typography>
            <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center', mb: 0.5 }}>
              <Typography variant="body2" color="text.secondary">
                {board.unassignedCount}
              </Typography>
              {allocDirty ? (
                <Chip size="small" label={t('config.board.draftEdits', 'Draft edits')} />
              ) : null}
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              {t(
                'config.board.unassignedNote',
                'Products in no family of this grouping.',
              )}
            </Typography>
            <Box sx={{ maxHeight: '52vh', overflowY: 'auto' }}>
              {unassignedShown.map((product) => (
                <ProductCard
                  key={product.sourceProductId}
                  product={product}
                  removable={false}
                  familyName=""
                  onRemove={() => undefined}
                />
              ))}
            </Box>
            {hiddenRetired > 0 ? (
              <Box sx={{ mt: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  {t('config.board.hiddenRetired', '{{count}} retired products hidden', {
                    count: hiddenRetired,
                  })}
                </Typography>
                <Button size="small" onClick={() => setShowRetired(true)}>
                  {t('config.board.showRetired', 'Show retired')}
                </Button>
              </Box>
            ) : null}
          </Paper>

          {familiesDraft.map((family) => {
            const resolved = familyColorOrNeutral(family.color);
            const serverCount = familyProductCount.get(family.slug) ?? family.productCount;
            const draftChip = familyAllocChanged(
              seed,
              draft,
              family.slug,
              board.products.map((p) => p.sourceProductId),
            );
            const members = board.products.filter(
              (p) => draft.get(p.sourceProductId)?.has(family.slug) === true,
            );
            return (
              <Paper
                key={family.slug}
                variant="outlined"
                data-testid={`col-${family.slug}`}
                onDragOver={
                  readOnly
                    ? undefined
                    : (e) => {
                        e.preventDefault();
                        setDragOverSlug(family.slug);
                      }
                }
                onDragLeave={readOnly ? undefined : () => setDragOverSlug(null)}
                onDrop={readOnly ? undefined : (e) => onDropFamily(family.slug, e)}
                sx={{
                  width: 246,
                  flex: 'none',
                  p: 0,
                  borderColor:
                    dragOverSlug === family.slug || dragIds !== null
                      ? 'primary.main'
                      : undefined,
                  borderWidth:
                    dragOverSlug === family.slug || dragIds !== null ? 2 : undefined,
                  overflow: 'hidden',
                }}
              >
                <Box
                  sx={{
                    height: 4,
                    bgcolor: resolved !== null ? resolved.bg : 'divider',
                  }}
                />
                <Box sx={{ p: 1.5 }}>
                  <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
                    <Box
                      sx={
                        resolved !== null
                          ? {
                              width: 12,
                              height: 12,
                              borderRadius: '50%',
                              bgcolor: resolved.bg,
                              flex: '0 0 auto',
                            }
                          : {
                              width: 12,
                              height: 12,
                              borderRadius: '50%',
                              border: '1px solid',
                              borderColor: 'divider',
                              flex: '0 0 auto',
                            }
                      }
                    />
                    <Typography variant="subtitle1" sx={{ flex: 1, minWidth: 0 }}>
                      {family.displayName}
                    </Typography>
                    {!readOnly ? (
                      <>
                        <IconButton
                          size="small"
                          aria-label={t('config.board.addProducts', 'Add products to {{family}}', {
                            family: family.displayName,
                          })}
                          disabled={busy}
                          onClick={() => {
                            setPickerSlug(family.slug);
                            setPickerSearch('');
                            setPickerSelected(new Set());
                          }}
                        >
                          <Icon name="add" />
                        </IconButton>
                        <IconButton
                          size="small"
                          aria-label={t('config.board.colMenu', 'Family options')}
                          disabled={busy}
                          onClick={() =>
                            setMenuSlug((cur) => (cur === family.slug ? null : family.slug))
                          }
                        >
                          <Icon name="more_vert" />
                        </IconButton>
                      </>
                    ) : null}
                  </Stack>
                  <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center', mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      {serverCount}
                    </Typography>
                    {draftChip ? (
                      <Chip size="small" label={t('config.board.draftEdits', 'Draft edits')} />
                    ) : null}
                    {family.role === 'MEMBERSHIP' ? (
                      <Chip
                        size="small"
                        label={t('config.board.roleLabel', 'Membership')}
                      />
                    ) : null}
                  </Stack>

                  {menuSlug === family.slug && !readOnly ? (
                    <FamilyMenu
                      family={family}
                      busy={busy}
                      confirmDel={confirmDelFam === family.slug}
                      onRename={(name) => {
                        markStructureDirty(
                          familiesDraft.map((f) =>
                            f.slug === family.slug ? { ...f, displayName: name } : f,
                          ),
                        );
                      }}
                      onColor={(color) => {
                        markStructureDirty(
                          familiesDraft.map((f) =>
                            f.slug === family.slug ? { ...f, color } : f,
                          ),
                        );
                      }}
                      onMembership={() => {
                        markStructureDirty(
                          familiesDraft.map((f) =>
                            f.slug === family.slug
                              ? { ...f, role: f.role === 'MEMBERSHIP' ? null : 'MEMBERSHIP' }
                              : f,
                          ),
                        );
                      }}
                      onAskDelete={() => setConfirmDelFam(family.slug)}
                      onCancelDelete={() => setConfirmDelFam(null)}
                      onConfirmDelete={() => deleteFamily(family.slug)}
                      productCount={serverCount}
                    />
                  ) : null}

                  <Box sx={{ maxHeight: '52vh', overflowY: 'auto' }}>
                    {members.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">
                        {t('config.board.noneInFamily', 'No products in this family.')}
                      </Typography>
                    ) : (
                      members.map((product) => (
                        <ProductCard
                          key={product.sourceProductId}
                          product={product}
                          removable={!readOnly}
                          familyName={family.displayName}
                          onRemove={() => removeFrom(product.sourceProductId, family.slug)}
                          busy={busy}
                        />
                      ))
                    )}
                  </Box>
                </Box>
              </Paper>
            );
          })}

          {!readOnly ? (
            <Paper
              variant="outlined"
              data-testid="add-family-card"
              sx={{
                width: 246,
                flex: 'none',
                borderStyle: 'dashed',
                p: 1.5,
              }}
            >
              <Typography variant="subtitle1" sx={{ mb: 1 }}>
                {t('config.board.addNewFamily', 'Add new family')}
              </Typography>
              <TextField
                size="small"
                fullWidth
                placeholder={t('config.board.familyNamePh', 'Family name')}
                value={newFamilyName}
                disabled={busy}
                onChange={(e) => setNewFamilyName(e.target.value)}
                sx={{ mb: 1 }}
              />
              <Button
                variant="outlined"
                size="small"
                disabled={busy || newFamilyName.trim() === ''}
                onClick={createFamily}
              >
                {t('config.board.create', 'Create')}
              </Button>
            </Paper>
          ) : null}
        </Box>
      </Box>

      {dirty && !readOnly ? (
        <Box
          data-testid="save-bar"
          sx={{
            position: 'fixed',
            left: { xs: 0, md: NAV_WIDTH },
            right: 0,
            bottom: 0,
            bgcolor: 'background.paper',
            borderTop: '1px solid',
            borderColor: 'divider',
            px: 2,
            py: 1.5,
            zIndex: 10,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            flexWrap: 'wrap',
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography>
              {[
                structureDirty
                  ? t('config.board.pendingFam', '{{count}} family changes', {
                      count: pendingFam,
                    })
                  : null,
                allocDirty
                  ? t('config.board.pendingAlloc', '{{count}} product changes', {
                      count: allocChangeCount,
                    })
                  : null,
              ]
                .filter(Boolean)
                .join(' · ')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t(
                'config.board.buildNote',
                'Saving queues a report rebuild (a few minutes).',
              )}
            </Typography>
          </Box>
          <Button variant="outlined" disabled={busy} onClick={discard}>
            {t('config.board.discard', 'Discard')}
          </Button>
          <Button variant="contained" disabled={busy} onClick={() => void onSave()}>
            {t('config.board.saveBuild', 'Save and build')}
          </Button>
        </Box>
      ) : null}

      <Dialog
        open={pickerSlug !== null}
        onClose={() => setPickerSlug(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {t('config.board.addProducts', 'Add products to {{family}}', {
            family: pickerFamily?.displayName ?? '',
          })}
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            size="small"
            margin="dense"
            label={t('config.board.searchProducts', 'Search products')}
            value={pickerSearch}
            onChange={(e) => setPickerSearch(e.target.value)}
          />
          {pickerShown.length === 0 ? (
            <Typography color="text.secondary" sx={{ mt: 2 }}>
              {t('config.board.noMatch', 'No matching products.')}
            </Typography>
          ) : (
            <Stack sx={{ mt: 1, maxHeight: 360, overflowY: 'auto' }}>
              {pickerShown.map((product) => {
                const checked = pickerSelected.has(product.sourceProductId);
                return (
                  <FormControlLabel
                    key={product.sourceProductId}
                    data-testid={`picker-row-${product.sourceProductId}`}
                    control={
                      <Checkbox
                        checked={checked}
                        slotProps={{
                          input: { 'aria-label': textOrDash(product.name) },
                        }}
                        onChange={(_e, on) => {
                          setPickerSelected((prev) => {
                            const next = new Set(prev);
                            if (on) next.add(product.sourceProductId);
                            else next.delete(product.sourceProductId);
                            return next;
                          });
                        }}
                      />
                    }
                    label={textOrDash(product.name)}
                  />
                );
              })}
            </Stack>
          )}
          {pickerOverflow ? (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {t('config.board.refine', 'Refine your search to see more.')}
            </Typography>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPickerSlug(null)}>{t('common.cancel', 'Cancel')}</Button>
          <Button
            data-testid="picker-add"
            disabled={pickerSelected.size === 0 || pickerSlug === null}
            onClick={() => {
              if (pickerSlug === null) return;
              const ids = [...pickerSelected];
              const family = pickerSlug;
              addToMany(ids, family);
              setPickerSlug(null);
              setPickerSelected(new Set());
            }}
          >
            {t('config.board.addSelected', 'Add {{count}} selected', {
              count: pickerSelected.size,
            })}
          </Button>
        </DialogActions>
      </Dialog>

      <UnsavedChangesDialog
        open={backPrompt}
        onStay={() => setBackPrompt(false)}
        onLeave={() => {
          setBackPrompt(false);
          discard();
          onBack();
        }}
      />

      <Snackbar open={notice !== null} autoHideDuration={6000} onClose={() => setNotice(null)}>
        <Alert severity="success" onClose={() => setNotice(null)}>
          {notice === 'savedQueued'
            ? t('config.classification.savedQueued', 'Saved. Report rebuild queued.')
            : t('config.classification.saved', 'Saved.')}
        </Alert>
      </Snackbar>
    </Box>
  );
}

function ProductCard({
  product,
  removable,
  familyName,
  onRemove,
  busy = false,
}: {
  product: DimensionBoardDto['products'][number];
  removable: boolean;
  familyName: string;
  onRemove: () => void;
  busy?: boolean;
}) {
  const { t } = useCopy();
  const status = effectiveStatus(product);
  const name = textOrDash(product.name);
  const meta = [
    textOrDash(product.productType),
    product.durationDays === null
      ? EM_DASH
      : t('config.board.days', '{{count}} days', { count: product.durationDays }),
    product.hasPendingVisits === true ? t('config.board.visitPack', 'Visit pack') : null,
    product.sourceSystem === 'LEGACY' ? t('config.board.legacy', 'Legacy') : null,
  ]
    .filter((part) => part !== null)
    .join(' · ');

  return (
    <Box
      data-testid={`card-${product.sourceProductId}`}
      sx={{
        py: 1,
        borderBottom: '1px solid',
        borderColor: 'divider',
        opacity: isFaded(status) ? 0.55 : 1,
        display: 'flex',
        alignItems: 'flex-start',
        gap: 0.5,
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography>{name}</Typography>
        <Typography variant="caption" color="text.secondary">
          {meta}
        </Typography>
        <CatalogueStatusChip status={status} sx={{ mt: 0.5 }} />
      </Box>
      {removable ? (
        <IconButton
          size="small"
          disabled={busy}
          data-testid={`remove-${product.sourceProductId}`}
          aria-label={t('config.board.removeFrom', 'Remove from {{family}}', {
            family: familyName,
          })}
          onClick={onRemove}
        >
          <Icon name="close" />
        </IconButton>
      ) : null}
    </Box>
  );
}

function FamilyMenu({
  family,
  busy,
  confirmDel,
  onRename,
  onColor,
  onMembership,
  onAskDelete,
  onCancelDelete,
  onConfirmDelete,
  productCount,
}: {
  family: StructureFamily;
  busy: boolean;
  confirmDel: boolean;
  onRename: (name: string) => void;
  onColor: (color: string | null) => void;
  onMembership: () => void;
  onAskDelete: () => void;
  onCancelDelete: () => void;
  onConfirmDelete: () => void;
  productCount: number;
}) {
  const { t } = useCopy();
  const [name, setName] = useState(family.displayName);
  useEffect(() => {
    setName(family.displayName);
  }, [family.displayName]);

  return (
    <Box
      data-testid={`menu-${family.slug}`}
      sx={{ mb: 1, p: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
    >
      <TextField
        size="small"
        fullWidth
        disabled={busy}
        value={name}
        onChange={(e) => setName(e.target.value)}
        onBlur={() => {
          const trimmed = name.trim();
          if (trimmed !== '' && trimmed !== family.displayName) onRename(trimmed);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            const trimmed = name.trim();
            if (trimmed !== '' && trimmed !== family.displayName) onRename(trimmed);
          }
        }}
        sx={{ mb: 1 }}
      />
      <Stack direction="row" sx={{ flexWrap: 'wrap', mb: 1, maxWidth: 200 }}>
        {FAMILY_PALETTE.map((hex) => (
          <IconButton
            key={hex}
            aria-label={hex}
            disabled={busy}
            onClick={() => onColor(hex)}
            sx={{
              width: 28,
              height: 28,
              bgcolor: hex,
              '&:hover': { bgcolor: hex, opacity: 0.85 },
            }}
          />
        ))}
      </Stack>
      <Button size="small" disabled={busy} onClick={() => onColor(null)} sx={{ mb: 1 }}>
        {t('config.classification.noColour', 'No colour')}
      </Button>
      <FormControlLabel
        control={
          <Switch
            checked={family.role === 'MEMBERSHIP'}
            disabled={busy}
            onChange={() => onMembership()}
          />
        }
        label={t('config.board.useForMembership', 'Use for Membership report')}
      />
      <FormHelperText sx={{ mt: -0.5, mb: 1 }}>
        {t(
          'config.board.membershipHelp',
          'Products in this family count as memberships in Membership Stats. You can mark more than one family.',
        )}
      </FormHelperText>
      {confirmDel ? (
        <Box sx={{ mt: 1 }}>
          <Typography variant="body2" sx={{ mb: 1 }}>
            {t('config.board.confirmDelFam', '{{count}} products lose this allocation.', {
              count: productCount,
            })}
          </Typography>
          <Stack direction="row" spacing={1}>
            <Button size="small" onClick={onCancelDelete}>
              {t('common.cancel', 'Cancel')}
            </Button>
            <Button size="small" color="error" disabled={busy} onClick={onConfirmDelete}>
              {t('config.board.deleteFamily', 'Delete family')}
            </Button>
          </Stack>
        </Box>
      ) : (
        <Button size="small" color="error" disabled={busy} onClick={onAskDelete} sx={{ mt: 1 }}>
          {t('config.board.deleteFamily', 'Delete family')}
        </Button>
      )}
    </Box>
  );
}
